import React, { useState, useEffect, memo, ReactElement } from 'react';
import i18next from 'i18next';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import { isEmpty } from 'lodash';
import Answer from './Answer';
import { Alert, Badge, Button, Card, Col, Modal, Row } from 'react-bootstrap';
import LoadingSpinner from '../../../components/LoadingSpinner';
import EmptyState from '../../../components/EmptyState';
import TrainingPlanAccordion from './TrainingPlanAccordion';
import { EnduranceTrainingDto, TrainingDto, TrainingPlanDto } from '../../../../../shared/dtos/trainingplan.dto';
import { ExerciseType, TrainingPlanStatus } from '../../../../../shared/utils/enums';
import { PromGraphDto } from '../../../../../shared/dtos/prom.dto';
import { TrainingExerciseDto } from 'shared/dtos/trainingExercise.dto';
import { TherapyStatisticDto, TrainingPlanStatisticDto } from '../../../../../shared/dtos/therapyStatistic.dto';
import { formatDate } from '../../../util/date-utils';

const AnswerAndActivity = ({ therapyId }: { therapyId: string }): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();
  const [promGraphs, setPromGraphs] = useState<PromGraphDto[]>([]);
  const [trainingPlanList, setTrainingPlanList] = useState<TrainingPlanDto[]>([]);
  const [statisticByPlanId, setStatisticByPlanId] = useState<Map<string, TrainingPlanStatisticDto>>(new Map());
  const [selectedTrainingPlan, setSelectedTrainingPlan] = useState<TrainingPlanDto>();
  const [selectedId, setSelectedId] = useState<string[]>([]);
  const [showTrainingPlan, setShowTrainingPlan] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);


  let displayDate: string = '';

  if (selectedTrainingPlan) {
    if (selectedTrainingPlan.status === TrainingPlanStatus.PUBLISHED && selectedTrainingPlan.endDate) {
      displayDate = formatDate(selectedTrainingPlan.endDate);
    } else if (selectedTrainingPlan.status === TrainingPlanStatus.UNPUBLISHED && selectedTrainingPlan.unpublishedAt) {
      displayDate = formatDate(selectedTrainingPlan.unpublishedAt);
    }
  }

  const fetchTrainingPlan = async (therapyId: string): Promise<void> => {
    const trainingPlanList: TrainingPlanDto[] = await authFetch(`/api/v1/therapies/${therapyId}/training-plans`);
    setTrainingPlanList(trainingPlanList);

    if (trainingPlanList.length > 0) {
      setSelectedTrainingPlan(trainingPlanList[0]);
      setSelectedId((selectedId: string[]) => [...selectedId, trainingPlanList[0].id]);
    }
  };

  const fetchAnswers = async (therapyId: string): Promise<void> => {
    const response: PromGraphDto[] = await authFetch(`/api/v1/promquestions/therapy/${therapyId}/analysis/graph`);
    setPromGraphs(response);
  };

  const fetchStatistic = async (therapyId: string): Promise<void> => {
    const statistic: TherapyStatisticDto = await authFetch(`/api/v1/therapies/${therapyId}/statistic`);
    setStatisticByPlanId(new Map(statistic.trainingPlans.map((plan: TrainingPlanStatisticDto) => [plan.trainingPlanId, plan])));
  };

  const handleViewTrainingPlan = (trainingPlan: TrainingPlanDto): void => {
    setSelectedTrainingPlan(trainingPlan);
    setShowTrainingPlan(true);
  };

  useEffect(() => {
    const fetchDataAsync = async (): Promise<void> => {
      try {
        await fetchTrainingPlan(therapyId);
        await fetchAnswers(therapyId);
        await fetchStatistic(therapyId);
      } catch (error) {
        console.error('Error occured while fetching answers and activity', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchDataAsync();
  }, [therapyId]);

  const handleTrainingSelected = (id: string, index: number): void => {
    if (index < 0) {
      setSelectedId((selectedId: string[]) => [...selectedId, id]);
    } else {
      const temp: string[] = [...selectedId];
      temp.splice(index, 1);
      setSelectedId(temp);
    }

  };

  const renderGraph = (graph: PromGraphDto, index: number): ReactElement | null => {
    return (
      <Col md={6} className="mb-3" key={index}>
        <Answer promGraph={graph}/>
      </Col>
    );
  };

  const renderExerciseModalBody = (exercise: TrainingExerciseDto, index: number): ReactElement => {
    const showInfo: boolean = exercise.exerciseType !== ExerciseType.RELAXATION;

    const sets: string = showInfo && exercise.sets ? `${exercise.sets} x ` : '';
    const time: string = showInfo && exercise.time ? `${exercise.time} ${exercise.unit}` : '';
    const reps: string = showInfo && !exercise.time && exercise.repetition ? `${exercise.repetition}` : '';

    return (
      <p key={'exercise_' + index} className='small ps-1 mb-1'>
        {exercise.name}
        <span className="float-end">
          {sets}
          {time || reps}
        </span>
      </p>
    );
  };

  const renderTrainingPlanModalBody = (training: TrainingDto, index: number): ReactElement => {
    return (
      <div key={'training_' + index}>
        {index !== 0 ? <p></p> : null}
        <p className='mb-0'><strong>{training.name}</strong></p>
        <p className='small pb-1 ps-1'>
          {`${training.workoutsPerWeek}x ${i18next.t('answerAndActivity.viewTrainingPlanModal.workoutsPerWeek')}, `}
          {training.workoutPause} {i18next.t('answerAndActivity.viewTrainingPlanModal.workoutPause')}
        </p>
        {training.exercises ? training.exercises.map((exercise: TrainingExerciseDto, j: number) => renderExerciseModalBody(exercise, j)) : null}
      </div>
    );
  };

  const renderEnduranceTrainingModalBody = (enduranceTraining: EnduranceTrainingDto, index: number): ReactElement => {
    const typeName = i18next.language === 'en'
      ? enduranceTraining.enduranceTrainingType.enName
      : enduranceTraining.enduranceTrainingType.deName;

    return (
      <div key={'endurance_training_' + index}>
        <p className='mb-0'><strong>{enduranceTraining.name}</strong></p>
        <p className='small pb-1 ps-1'>
          {`${enduranceTraining.workoutsPerWeek}x ${i18next.t('answerAndActivity.viewTrainingPlanModal.workoutsPerWeek')}`}
        </p>
        <p className='small ps-1'>
          {typeName}
          <span className="float-end">{enduranceTraining.enduranceTrainingType.trainingMinutes} min</span>
        </p>
      </div>
    );
  };

  const renderTrainingPlan = (trainingPlan: TrainingPlanDto, index: number): ReactElement => {
    let displayDate: string = '';

    if (trainingPlan.status === TrainingPlanStatus.PUBLISHED && trainingPlan.endDate) {
      displayDate = formatDate(trainingPlan.endDate);
    } else if (trainingPlan.unpublishedAt) {
      displayDate = formatDate(trainingPlan.unpublishedAt);
    }

    return (
      <span key={'tp_' + index}>
        <Card key={'tp_' + index} className="bg-training-strip mb-2 pb-3 border-0">
          <Row className="p-1 ps-3 pe-3">
            <Col
              md={9}
              role="button"
              tabIndex={0}
              aria-expanded={selectedId.indexOf(trainingPlan.id) >= 0}
              className="align-middle pt-3 pointer bg-training-strip-text"
              onClick={() => {
                handleTrainingSelected(trainingPlan.id, selectedId.indexOf(trainingPlan.id));
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleTrainingSelected(trainingPlan.id, selectedId.indexOf(trainingPlan.id));
                }
              }}
              key={'t_' + index + '_' + selectedId}>
              {selectedId.indexOf(trainingPlan.id) >= 0 ?
                <i className="fas fa-angle-up" aria-hidden="true"/> :
                <i className="fas fa-angle-down" aria-hidden="true"/>
              }
              {trainingPlan.startDate
                ? i18next.t('answerAndActivity.trainingPlanFrom', {
                  startDate: formatDate(trainingPlan.startDate),
                  endDate: displayDate
                })
                : null
              }
              {trainingPlan.status === TrainingPlanStatus.PUBLISHED
                ? <Badge bg="success" className="ms-2">{i18next.t('trainingPlan.status.published')}</Badge>
                : null}
            </Col>
            <Col md={3}>
              <Button
                size="sm"
                variant="link"
                className="text-primary text-decoration-none float-end mt-3 button-font"
                onClick={() => handleViewTrainingPlan(trainingPlan)}>
                {i18next.t('answerAndActivity.viewTrainingPlanBtn')}
                <i className="fas fa-angle-right ms-1" aria-hidden="true" />
              </Button>
            </Col>
          </Row>
        </Card>
        {selectedId.indexOf(trainingPlan.id) >= 0 ?
          <div className="ps-4 pe-2 pb-3">
            <TrainingPlanAccordion statistic={statisticByPlanId.get(trainingPlan.id)}/>
          </div> : null}
      </span>
    );
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (hasError) {
    return <Alert variant="danger">{i18next.t('generic.error')}</Alert>;
  }

  return (
    <main className="d-flex flex-grow-1 flex-column">
      <div
        className="d-flex gap-2 align-items-start rounded-3 p-3 mb-3"
        role="alert"
        style={{
          backgroundColor: '#fff8e6',
          border: '1px solid #f5e2b3',
          borderLeft: '4px solid #e8b84b',
          color: '#6b5a2b',
        }}
      >
        <i className="fas fa-exclamation-triangle mt-1" style={{ color: '#d99b1c' }} aria-hidden="true" />
        <p className="mb-0 small">{i18next.t('answerAndActivity.warningText')}</p>
      </div>

      <div className="bg-white border rounded-3 p-3 mb-3">
        <h2 className="fw-bold fs-4 mb-3">{i18next.t('answerAndActivity.answerTitle')}</h2>
        {promGraphs && !isEmpty(promGraphs) ? (
          <Row>{promGraphs.map((graph: PromGraphDto, index: number) => renderGraph(graph, index))}</Row>
        ) : (
          <EmptyState icon="fas fa-poll" message={i18next.t('answerAndActivity.noAnswer')} />
        )}
      </div>

      <div className="bg-white border rounded-3 p-3">
        <h2 className="fw-bold fs-4 mb-3">{i18next.t('answerAndActivity.activityTitle')}</h2>
        {trainingPlanList.length > 0 ? (
          trainingPlanList.map((value: TrainingPlanDto, index: number) => renderTrainingPlan(value, index))
        ) : (
          <EmptyState icon="fas fa-dumbbell" message={i18next.t('answerAndActivity.noActivity')} />
        )}
      </div>

      {selectedTrainingPlan ?
        <Modal className="p-1" show={showTrainingPlan} onHide={() => setShowTrainingPlan(false)}
          key={selectedTrainingPlan.startDate}>
          <Modal.Header closeButton className="bg-light">
            <Modal.Title>
              <h3>
                {selectedTrainingPlan.startDate
                  ? i18next.t('answerAndActivity.trainingPlanFrom', {
                    startDate: formatDate(selectedTrainingPlan.startDate),
                    endDate: displayDate
                  })
                  : null
                }
              </h3>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="m-2">
            <div className="overflow-x-hidden pe-2" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
              {selectedTrainingPlan
                ? [
                    ...selectedTrainingPlan.trainings.map((training: TrainingDto, index: number) => ({
                      order: training.order,
                      element: renderTrainingPlanModalBody(training, index),
                    })),
                    ...selectedTrainingPlan.enduranceTrainings.map((enduranceTraining: EnduranceTrainingDto, index: number) => ({
                      order: enduranceTraining.order,
                      element: renderEnduranceTrainingModalBody(enduranceTraining, index),
                    })),
                  ]
                    .sort((a, b) => a.order - b.order)
                    .map((entry) => entry.element)
                : null
              }
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowTrainingPlan(false)}>
              {i18next.t('generic.close')}
            </Button>
          </Modal.Footer>
        </Modal>
        : null}
    </main>
  )
};

export default memo(AnswerAndActivity);
